import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  LineChart, Line,
  ScatterChart, Scatter, ZAxis,
  AreaChart, Area
} from 'recharts';
import './Analytics.css';
import DUAModal, { useDUACheck } from '../components/DUAModal';

const API = '/api/v2';
const COLORS = ['#2563eb','#7c3aed','#059669','#d97706','#dc2626','#0891b2','#be185d','#4f46e5','#65a30d','#ea580c'];

function Analytics() {
  const { needsDUA, checked: duaChecked, refresh: refreshDUA } = useDUACheck();

  const [campaigns, setCampaigns] = useState([]);
  const [slug, setSlug] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(API + '/campaigns').then(r => {
      const c = r.data.campaigns || [];
      setCampaigns(c);
      if (c.length) loadAnalytics(c[0].slug);
    });
  }, []);

  const loadAnalytics = async (s) => {
    setSlug(s); setLoading(true);
    try {
      const r = await axios.get(API + '/campaigns/' + s + '/analytics');
      setData(r.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    if (needsDUA) return (<div style={{minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center'}}><DUAModal onSigned={refreshDUA} /></div>);
    if (!duaChecked) return null;

    return (
      <div className="chart-tooltip">
        <p className="ct-label">{label}</p>
        {payload.map((p, i) => <p key={i} style={{color: p.color}}>{p.name}: <strong>{p.value}</strong></p>)}
      </div>
    );
  };

  const PieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    if (percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={600}>{Math.round(percent * 100)}%</text>;
  };

  if (!data && !loading) return <div className="analytics-container"><h1>Analytics</h1><p>Loading campaigns...</p></div>;

  return (
    <div className="analytics-container">
      <div className="analytics-header">
        <h1>Analytics</h1>
        <select className="campaign-picker" value={slug} onChange={e => loadAnalytics(e.target.value)}>
          {campaigns.map(c => <option key={c.slug} value={c.slug}>{c.name} ({c.patient_count})</option>)}
        </select>
      </div>

      {loading && <div className="loading-bar">Loading analytics...</div>}

      {data && (
        <>
          <div className="stats-row">
            <div className="stat-card accent">
              <span className="stat-val">{data.total_patients}</span>
              <span className="stat-label">Total Patients</span>
            </div>
            {data.numericStats.age_at_surgery && (
              <div className="stat-card">
                <span className="stat-val">{data.numericStats.age_at_surgery.mean}</span>
                <span className="stat-label">Avg Age</span>
                <span className="stat-sub">{data.numericStats.age_at_surgery.min}–{data.numericStats.age_at_surgery.max}</span>
              </div>
            )}
            {data.numericStats.bmi && (
              <div className="stat-card">
                <span className="stat-val">{data.numericStats.bmi.mean}</span>
                <span className="stat-label">Avg BMI</span>
                <span className="stat-sub">{data.numericStats.bmi.min}–{data.numericStats.bmi.max}</span>
              </div>
            )}
            {data.numericStats.preop_odi && (
              <div className="stat-card">
                <span className="stat-val">{data.numericStats.preop_odi.mean}</span>
                <span className="stat-label">Avg Pre-op ODI</span>
                <span className="stat-sub">n={data.numericStats.preop_odi.count}</span>
              </div>
            )}
            {data.numericStats.preop_vas_back && (
              <div className="stat-card">
                <span className="stat-val">{data.numericStats.preop_vas_back.mean}</span>
                <span className="stat-label">Avg Pre-op VAS Back</span>
                <span className="stat-sub">n={data.numericStats.preop_vas_back.count}</span>
              </div>
            )}
            {data.numericStats.operative_time_min && (
              <div className="stat-card">
                <span className="stat-val">{data.numericStats.operative_time_min.mean}</span>
                <span className="stat-label">Avg Op Time (min)</span>
                <span className="stat-sub">{data.numericStats.operative_time_min.min}–{data.numericStats.operative_time_min.max}</span>
              </div>
            )}
          </div>

          <div className="chart-grid">
            {/* Age Distribution */}
            {data.ageHist.length > 0 && (
              <div className="chart-card">
                <h3>Age Distribution</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={data.ageHist}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="range" fontSize={11} />
                    <YAxis fontSize={11} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" fill="#2563eb" radius={[4,4,0,0]} name="Patients" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* BMI Distribution */}
            {data.bmiHist.length > 0 && (
              <div className="chart-card">
                <h3>BMI Distribution</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={data.bmiHist}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="range" fontSize={11} />
                    <YAxis fontSize={11} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" fill="#7c3aed" radius={[4,4,0,0]} name="Patients" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Sex Pie */}
            {data.distributions.sex && (
              <div className="chart-card">
                <h3>Sex Distribution</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={data.distributions.sex} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} labelLine={false} label={PieLabel}>
                      {data.distributions.sex.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Smoking Pie */}
            {data.distributions.smoking_status && (
              <div className="chart-card">
                <h3>Smoking Status</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={data.distributions.smoking_status} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} labelLine={false} label={PieLabel}>
                      {data.distributions.smoking_status.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Laterality Bar */}
            {data.distributions.laterality && (
              <div className="chart-card">
                <h3>Laterality</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={data.distributions.laterality} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis type="number" fontSize={11} />
                    <YAxis type="category" dataKey="name" fontSize={12} width={80} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" fill="#059669" radius={[0,4,4,0]} name="Patients" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* ASA Class */}
            {data.distributions.asa_class && (
              <div className="chart-card">
                <h3>ASA Classification</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={data.distributions.asa_class}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="name" fontSize={11} />
                    <YAxis fontSize={11} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" fill="#d97706" radius={[4,4,0,0]} name="Patients" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* ODI Timeline */}
            {data.odiTimeline.some(t => t.avg !== null) && (
              <div className="chart-card wide">
                <h3>ODI Score Over Time</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={data.odiTimeline.filter(t => t.avg !== null)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="timepoint" fontSize={11} />
                    <YAxis domain={[0, 100]} fontSize={11} />
                    <Tooltip content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      return <div className="chart-tooltip"><p className="ct-label">{label}</p><p>ODI: <strong>{payload[0].value}</strong></p><p style={{color:'#94a3b8',fontSize:'0.75rem'}}>n={data.odiTimeline.find(t=>t.timepoint===label)?.n}</p></div>;
                    }} />
                    <Area type="monotone" dataKey="avg" stroke="#2563eb" fill="#dbeafe" strokeWidth={2} name="Avg ODI" dot={{ r: 5, fill: '#2563eb' }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* VAS Back + Leg Timeline */}
            {(data.vasBackTimeline.some(t => t.avg !== null) || data.vasLegTimeline.some(t => t.avg !== null)) && (
              <div className="chart-card wide">
                <h3>VAS Pain Scores Over Time</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="timepoint" fontSize={11} type="category" allowDuplicatedCategory={false} />
                    <YAxis domain={[0, 10]} fontSize={11} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    {data.vasBackTimeline.some(t => t.avg !== null) && <Line data={data.vasBackTimeline.filter(t => t.avg !== null)} type="monotone" dataKey="avg" stroke="#dc2626" strokeWidth={2} name="VAS Back" dot={{ r: 5 }} />}
                    {data.vasLegTimeline.some(t => t.avg !== null) && <Line data={data.vasLegTimeline.filter(t => t.avg !== null)} type="monotone" dataKey="avg" stroke="#2563eb" strokeWidth={2} name="VAS Leg" dot={{ r: 5 }} />}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* ── ARM COMPARISON ── */}
            {data.armComparison && data.armComparison.arms.length > 1 && (
              <>
                <div className="section-divider wide">
                  <h2>Arm Comparison: {data.armComparison.arms.map(a => a.name + ' (n=' + a.n + ')').join(' vs ')}</h2>
                </div>

                {/* ODI by Arm */}
                {data.armComparison.odiByArm.some(r => data.armComparison.arms.some(a => r[a.name] !== null)) && (
                  <div className="chart-card wide">
                    <h3>ODI Score by Arm</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={data.armComparison.odiByArm}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="timepoint" fontSize={11} />
                        <YAxis domain={[0, 80]} fontSize={11} label={{ value: 'ODI', angle: -90, position: 'insideLeft', fontSize: 11 }} />
                        <Tooltip content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          return <div className="chart-tooltip"><p className="ct-label">{label}</p>{payload.filter(p => p.value !== null).map((p, i) => <p key={i} style={{color: p.color}}>{p.name}: <strong>{p.value}</strong> (n={data.armComparison.odiByArm.find(r => r.timepoint === label)?.[p.name + '_n'] || 0})</p>)}</div>;
                        }} />
                        <Legend />
                        {data.armComparison.arms.map((a, i) => <Line key={a.slug} type="monotone" dataKey={a.name} stroke={COLORS[i]} strokeWidth={2} dot={{ r: 5 }} connectNulls />)}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* VAS Back by Arm */}
                {data.armComparison.vasBackByArm.some(r => data.armComparison.arms.some(a => r[a.name] !== null)) && (
                  <div className="chart-card wide">
                    <h3>VAS Back Pain by Arm</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={data.armComparison.vasBackByArm}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="timepoint" fontSize={11} />
                        <YAxis domain={[0, 10]} fontSize={11} label={{ value: 'VAS', angle: -90, position: 'insideLeft', fontSize: 11 }} />
                        <Tooltip content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          return <div className="chart-tooltip"><p className="ct-label">{label}</p>{payload.filter(p => p.value !== null).map((p, i) => <p key={i} style={{color: p.color}}>{p.name}: <strong>{p.value}</strong> (n={data.armComparison.vasBackByArm.find(r => r.timepoint === label)?.[p.name + '_n'] || 0})</p>)}</div>;
                        }} />
                        <Legend />
                        {data.armComparison.arms.map((a, i) => <Line key={a.slug} type="monotone" dataKey={a.name} stroke={COLORS[i]} strokeWidth={2} dot={{ r: 5 }} connectNulls />)}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* VAS Leg by Arm */}
                {data.armComparison.vasLegByArm.some(r => data.armComparison.arms.some(a => r[a.name] !== null)) && (
                  <div className="chart-card wide">
                    <h3>VAS Leg Pain by Arm</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={data.armComparison.vasLegByArm}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="timepoint" fontSize={11} />
                        <YAxis domain={[0, 10]} fontSize={11} label={{ value: 'VAS', angle: -90, position: 'insideLeft', fontSize: 11 }} />
                        <Tooltip content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          return <div className="chart-tooltip"><p className="ct-label">{label}</p>{payload.filter(p => p.value !== null).map((p, i) => <p key={i} style={{color: p.color}}>{p.name}: <strong>{p.value}</strong> (n={data.armComparison.vasLegByArm.find(r => r.timepoint === label)?.[p.name + '_n'] || 0})</p>)}</div>;
                        }} />
                        <Legend />
                        {data.armComparison.arms.map((a, i) => <Line key={a.slug} type="monotone" dataKey={a.name} stroke={COLORS[i]} strokeWidth={2} dot={{ r: 5 }} connectNulls />)}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Satisfaction by Arm */}
                {data.armComparison.satisfactionByArm.length > 0 && (
                  <div className="chart-card wide">
                    <h3>Patient Satisfaction by Arm (% Satisfied or Very Satisfied)</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={data.armComparison.satisfactionByArm}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="timepoint" fontSize={11} />
                        <YAxis domain={[0, 100]} fontSize={11} label={{ value: '%', angle: -90, position: 'insideLeft', fontSize: 11 }} />
                        <Tooltip content={({ active, payload, label }) => {
                          if (!active || !payload?.length) return null;
                          return <div className="chart-tooltip"><p className="ct-label">{label}</p>{payload.filter(p => p.value !== null).map((p, i) => <p key={i} style={{color: p.fill}}>{p.name}: <strong>{p.value}%</strong> (n={data.armComparison.satisfactionByArm.find(r => r.timepoint === label)?.[p.name + '_n'] || 0})</p>)}</div>;
                        }} />
                        <Legend />
                        {data.armComparison.arms.map((a, i) => <Bar key={a.slug} dataKey={a.name} fill={COLORS[i]} radius={[4,4,0,0]} />)}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </>
            )}

                        {/* Scatter: Age vs ODI Improvement */}
            {data.scatter.length > 0 && (
              <div className="chart-card wide">
                <h3>Age vs ODI Improvement</h3>
                <p className="chart-sub">Each dot is a patient. Higher = more improvement.</p>
                <ResponsiveContainer width="100%" height={280}>
                  <ScatterChart>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="age" name="Age" fontSize={11} label={{ value: 'Age', position: 'bottom', fontSize: 11 }} />
                    <YAxis dataKey="improvement" name="ODI Improvement" fontSize={11} label={{ value: 'ODI Δ', angle: -90, position: 'insideLeft', fontSize: 11 }} />
                    <ZAxis range={[40, 40]} />
                    <Tooltip content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      return <div className="chart-tooltip"><p>Age: {d.age}, ODI Δ: {d.improvement}</p><p style={{color:'#94a3b8'}}>{d.sex}</p></div>;
                    }} />
                    <Scatter data={data.scatter.filter(d => d.sex === 'Female')} fill="#be185d" name="Female" />
                    <Scatter data={data.scatter.filter(d => d.sex === 'Male')} fill="#2563eb" name="Male" />
                    <Legend />
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Completeness */}
            {data.completenessHist && (
              <div className="chart-card">
                <h3>Data Completeness</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={data.completenessHist}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="range" fontSize={11} />
                    <YAxis fontSize={11} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" fill="#0891b2" radius={[4,4,0,0]} name="Patients" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
export default Analytics;
